<script lang="ts">
  import * as d3 from 'd3';
  import type { Pie } from '$lib/sanity/schemas';
  import ChartLayout from './components/ChartLayout.svelte';
  import Table from './components/Table.svelte';
  import { addDataLabels, setUpChart, type DataLabel } from './utils/chart';
  import { getPalette } from './utils/color';
  import { categorizeChartItems, formatDocumentId, formatGraphValue } from './utils/data';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import {
    INTERACTION_CLASS,
    barInteraction,
    interactionStore,
    labelInteraction,
    labelBackgroundInteraction
  } from './utils/interactions';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: Pie;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
    gridColspan: number;
  }

  let { document, sourceMap, path = [], gridColspan }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const {
    _id,
    alt,
    chart = null,
    chartType = 'pie',
    colorTheme = '',
    footnote = null,
    headline = '',
    prefix = '',
    subhead = '',
    suffix = ''
  } = document;
  const { items = [] } = chart || {};
  const categories = categorizeChartItems(items);
  const id = formatDocumentId(_id);
  const store = interactionStore(id);
  const colorPalette = getPalette(colorTheme, categories.primary.length);
  const isDonutChart = chartType === 'donut';
  const tableProps = {
    header: ['Category', 'Value'],
    rows: items.map((item) => [item.primaryCategory, item.value])
  };
  const renderChart = (chartElement: HTMLElement) => {
    const { selection, sizes } = setUpChart(chartElement, { alt, colorPalette });
    const values = items.map((item) => {
      return item.value || 0;
    });
    const pie = d3.pie();
    const arcs = pie(values);
    const innerRadius = sizes.chart.height / 2;
    const outerRadius = 150;
    const arcLabel = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);

    // Draw pie slices
    const slices = selection
      .append('g')
      .attr('transform', `translate(${sizes.chart.width / 2}, ${sizes.chart.height / 2})`);

    slices
      .selectAll('.pie-slices')
      .data(arcs)
      .join('path')
      .attr('fill', function (d) {
        const color = colorPalette?.colors[d.index];

        return colorPalette?.isGradient ? `url(#${color?.id})` : color?.color || '';
      })
      .attr('stroke', 'black')
      .attr('stroke-width', '2px')
      .attr('d', function (d: any) {
        const path = d3
          .arc()
          .innerRadius(innerRadius)
          .outerRadius(isDonutChart ? outerRadius : 0)
          .padAngle(isDonutChart ? 0.01 : 0)(d);

        return path || '';
      })
      .style('cursor', 'pointer')
      .each(function (d) {
        const color = colorPalette?.colors[d.index];
        const colorId = color?.id || '';
        const classname = `${INTERACTION_CLASS} ${barInteraction.classname} ${colorId}`;
        const interactions = [barInteraction, labelInteraction, labelBackgroundInteraction];
        const selected = colorId ? [colorId] : [];

        d3.select(this)
          .style('cursor', 'pointer')
          .attr('class', classname)
          .on('mouseover', function () {
            store.triggerInteraction(interactions, selected);
          })
          .on('mouseout', function () {
            store.resetInteraction(interactions);
          });
      });

    // Draw data labels
    const valueLabels: DataLabel[] = arcs.map(function (data): DataLabel {
      const arc = data as any;
      const [transX, transY] = arcLabel.centroid(arc);
      const value = arc.data as number;
      const color = colorPalette?.colors[arc.index];
      const selected = color?.id ? [color?.id] : [];
      const translateX = isDonutChart ? transX : transX * 0.8;
      const translateY = isDonutChart ? transY : transY * 0.8;

      return {
        hidden: value < 10,
        selected,
        text: formatGraphValue(value, prefix, suffix),
        // Recreate the translate in slices part of the graph
        x: sizes.chart.width / 2 + translateX,
        y: sizes.chart.height / 2 + translateY
      };
    });
    const categoryLabels: DataLabel[] = valueLabels.map(function (data, index): DataLabel {
      const item = items[index];

      return {
        ...data,
        hidden: true,
        selectedBackgroundColor: '#000000',
        selectedColor: '#ffffff',
        text: item.primaryCategory,
        y: data.y - 50
      };
    });

    addDataLabels(selection, categoryLabels, store);

    addDataLabels(selection, valueLabels, store);
  };
</script>

<ChartLayout
  {footnote}
  {headline}
  {id}
  {subhead}
  {renderChart}
  {path}
  {encodeDataAttribute}
  {gridColspan}
>
  {#snippet table()}
    <Table {...tableProps} {gridColspan} />
  {/snippet}
</ChartLayout>
